import type { ClientPreferencesComponentInput } from "@repo/shared/types";
import { toast } from "sonner";
import {
  usePublicClientPreferencesValue,
  useSavePublicClientPreferences,
} from "@/lib/hooks/use-public-flow/use-client-preferences";
import { usePublicFlowDefinition } from "@/lib/hooks/use-public-flow/use-flow-instance";
import { useT } from "@/lib/i18n/use-t";
import {
  type ClientPreferenceItem,
  ClientPreferencesWidget,
} from "@/pages/clients/detail/flows/instance/client-preferences-widget";
import { T } from "./client-preferences-renderer.i18n";

type ClientPreferencesRendererProps = {
  component: { config?: string };
  isCompleted: boolean;
  language: string;
  hasValidationError?: boolean;
  /** QL-188 — group answering note; null for non-group clients. */
  groupNote?: string | null;
};

export function ClientPreferencesRenderer({
  component,
  isCompleted,
  language,
  hasValidationError,
  groupNote,
}: ClientPreferencesRendererProps) {
  const { definition } = usePublicFlowDefinition();
  const { inputData, isLoading: valueLoading } = usePublicClientPreferencesValue();
  const save = useSavePublicClientPreferences();

  let config: { style?: "none" | "border" | "box"; title?: string; description?: string } = {};
  try {
    config = component.config ? JSON.parse(component.config) : {};
  } catch {
    // Invalid config, use defaults
  }

  const preferences =
    ((definition as any)?.portfolioCollection?.clientPreferences as ClientPreferenceItem[]) ?? [];

  const t = useT(T);

  const handleChange = (input: ClientPreferencesComponentInput) => {
    save.mutate(input, { onError: () => toast.error(t.saveFailed) });
  };

  if (valueLoading) return null;

  return (
    <ClientPreferencesWidget
      preferences={preferences}
      inputData={inputData}
      onChange={handleChange}
      disabled={isCompleted}
      style={config.style}
      title={config.title}
      description={config.description}
      language={language}
      hasValidationError={hasValidationError}
      groupNote={groupNote}
    />
  );
}
